import type { ScanFrequency } from '@/sections/settings/subscription/constant';

interface MSRPInputs {
  numberOfApplications: number;
  scanSchedule: ScanFrequency | string;
  numberOfAssets?: number;
  selectedTier?: string;
  numberOfAEVExercises?: number;
  numberOfRedTeams?: number;
  // Measured compute cost basis in $/asset/year AT DAILY CADENCE — produced by
  // resolveAssetComputeAnnualRate(). Omit (or pass a non-positive / non-finite
  // value) to fall back to DEFAULT_ASSET_COMPUTE_ANNUAL_RATE.
  assetComputeAnnualRate?: number;
}

interface PricingBreakdown {
  fqdnCost: number;
  assetCost: number;
  aevCost: number;
  redTeamCost: number;
}

interface MSRPResult {
  floor: number;
  msrp: number;
  // True when the asset (platform) line is clamped to the $13K minimum.
  // floorAssetCost is 0 for empty configs, so this is false when no assets.
  atPlatformFloor: boolean;
  breakdown: PricingBreakdown; // MSRP-level breakdown
  floorBreakdown: PricingBreakdown; // Floor-level breakdown
  aevFloor: number;
  aevMsrp: number;
  redTeamFloor: number;
  redTeamMsrp: number;
}

// Floor prices (absolute minimum sales cannot go below)
const FQDN_FLOOR_RATE = 1000; // $1,000/application (DAST target)

const AEV_EXERCISE_FLOOR_RATE = 25000; // $25K floor / $50K MSRP per adversary emulation exercise

const RED_TEAM_RATE = 100000; // $100K per red team (ludacris only) — flat (no MSRP multiplier)

// MSRP multiplier (2× floor) — used by FQDN and AEV pricing only.
const MSRP_MULTIPLIER = 2;

// Asset pricing (compute-based, with a margin-protected floor)
// Compute cost basis: $/asset/year at daily cadence; scales down by interval_days:
//   cost(annual) = round(numberOfAssets × rate ÷ interval_days)
// The basis is the tenant's MEASURED rate when we have one (see
// resolveAssetComputeAnnualRate), and DEFAULT_ASSET_COMPUTE_ANNUAL_RATE otherwise.
// Two guardrails on the asset-coverage line, take the higher (max):
//   • 80% gross-margin floor = 5 × cost   (sell-gate: never sell below 80% GM)
//   • $13K platform minimum  = ASSET_SCAN_MINIMUM (protects small accounts)
// MSRP (list) = 10 × cost → 90% GM, also never below $13K. Reps discount from
// MSRP toward the floor and GM stays ≥ 80% the whole way.
// FQDN (Application Scanning / DAST) and AEV keep their own pricing and stack on top.
// $1/asset/year of compute at daily cadence — the FALLBACK basis, used only when
// no measured rate for the tenant is available (new tenant, no cost data yet, or
// a caller that cannot fetch one, e.g. the Add Customer wizard).
export const DEFAULT_ASSET_COMPUTE_ANNUAL_RATE = 1;
const ASSET_FLOOR_MULTIPLIER = 5; // 5× cost → 80% gross-margin floor
const ASSET_MSRP_MULTIPLIER = 10; // 10× cost → 90% gross-margin list (MSRP)
const ASSET_SCAN_MINIMUM = 13000; // $13K/yr absolute minimum (small accounts)

// Scan interval in days per cadence (denominator for the annual asset floor).
const INTERVAL_DAYS: Record<string, number> = {
  daily: 1,
  weekly: 7,
  biweekly: 14,
  monthly: 30,
  quarterly: 90,
};

// Fall back to the default basis unless we were handed a usable measured rate.
// Covers undefined/null, 0, negatives, NaN and Infinity — a bad measurement must
// never zero out or inflate the price basis.
function effectiveAssetComputeRate(rate?: number): number {
  return typeof rate === 'number' && Number.isFinite(rate) && rate > 0
    ? rate
    : DEFAULT_ASSET_COMPUTE_ANNUAL_RATE;
}

/**
 * Normalize the backend's measured `cost_per_asset` onto the calculator's
 * daily-cadence basis.
 *
 * `cost_per_asset` is annual compute $/asset at the tenant's currently
 * configured cadence — the backend fills `scan_frequency` from the tenant's
 * present scan schedule (cost.go), NOT from the cadence that generated the
 * measured window's spend — whereas the calculator's basis is $/asset/yr
 * at DAILY cadence which it then divides by the CONFIGURED cadence's interval.
 * Feeding the measured number in raw would apply the cadence discount twice.
 * Multiplying by the measured cadence's interval undoes the discount already
 * baked in: an identity for daily tenants, and ×7 / ×14 / ×30 / ×90 otherwise.
 * Caveat: if the cadence changed during/after the measured window, this
 * conversion is skewed until a full window at the new cadence has elapsed;
 * ENG-5664 (backend-owned rate derivation + provenance) is the durable fix.
 *
 * Returns undefined when no usable measurement exists (caller then gets
 * DEFAULT_ASSET_COMPUTE_ANNUAL_RATE).
 */
export function resolveAssetComputeAnnualRate(input: {
  costPerAsset?: number;
  scanFrequency?: string;
}): number | undefined {
  const costPerAsset = input.costPerAsset;
  if (
    typeof costPerAsset !== 'number' ||
    !Number.isFinite(costPerAsset) ||
    costPerAsset <= 0
  ) {
    return undefined;
  }
  // Unknown/absent cadence → treat as daily (×1). This UNDERSTATES the basis
  // rather than overstating it, so an unrecognized cadence can never inflate a
  // quote.
  const measuredIntervalDays = INTERVAL_DAYS[input.scanFrequency ?? ''] ?? 1;
  return costPerAsset * measuredIntervalDays;
}

// Frequency multipliers applied to FQDN pricing ONLY.
// These are intentionally legacy/out-of-scope for the asset-pricing change —
// FQDN scaling keeps its existing per-cadence multipliers and MSRP_MULTIPLIER = 2.
// Weekly scans take 2 days to finish, so floor = 2/7 of daily (0.2857) and MSRP = 0.57 of daily
// Bi-weekly = 2/14 of daily (0.1429); msrp = weekly msrp ÷ 2 (0.285)
// Monthly = weekly/4 (1 scan per month): floor = 0.0714, msrp = 0.1425
// Quarterly = weekly/12 (1 scan per quarter): floor = 0.0238, msrp = 0.0475
const FREQUENCY_MULTIPLIERS: Record<string, { floor: number; msrp: number }> = {
  daily: { floor: 1.0, msrp: 1.0 },
  weekly: { floor: 0.2857, msrp: 0.57 },
  biweekly: { floor: 0.1429, msrp: 0.285 },
  monthly: { floor: 0.0714, msrp: 0.1425 },
  quarterly: { floor: 0.0238, msrp: 0.0475 },
};

// Shared, pure floor-rule check — single source of truth used by both
// useMSRPCalculator and the Add Customer wizard validators so the "$13K
// platform floor" rule cannot drift between surfaces. Callers that price against
// a measured cost basis must pass the SAME assetComputeAnnualRate they pass to
// useMSRPCalculator, or the verdict and the floor number would disagree; the
// wizard omits it and prices a not-yet-existing tenant on the $1 default.
export function isAtPlatformFloor(inputs: {
  numberOfAssets?: number;
  scanSchedule: ScanFrequency | string;
  assetComputeAnnualRate?: number;
}): boolean {
  const numberOfAssets = inputs.numberOfAssets || 0;
  if (numberOfAssets <= 0) return false;
  const intervalDays = INTERVAL_DAYS[inputs.scanSchedule] ?? 1;
  const assetComputeCost = Math.round(
    (numberOfAssets *
      effectiveAssetComputeRate(inputs.assetComputeAnnualRate)) /
      intervalDays
  );
  const floorAssetCost = Math.max(
    assetComputeCost * ASSET_FLOOR_MULTIPLIER,
    ASSET_SCAN_MINIMUM
  );
  return floorAssetCost === ASSET_SCAN_MINIMUM;
}

// Note: This is a pure calculation function despite the `use` prefix.
// The useMemo was removed (React 19 Compiler handles memoization).
// Keeping the hook name for import stability across the codebase.
export function useMSRPCalculator(inputs: MSRPInputs): MSRPResult {
  // FQDN frequency multiplier (legacy/out-of-scope). Assets no longer use this.
  const multiplier = FREQUENCY_MULTIPLIERS[inputs.scanSchedule] || {
    floor: 1,
    msrp: 1,
  };

  const numberOfAssets = inputs.numberOfAssets || 0;
  const numberOfApplications = inputs.numberOfApplications || 0;

  const intervalDays = INTERVAL_DAYS[inputs.scanSchedule] ?? 1;
  const assetComputeCost = Math.round(
    (numberOfAssets *
      effectiveAssetComputeRate(inputs.assetComputeAnnualRate)) /
      intervalDays
  );
  // Floor: 80% gross-margin point (5× cost), never below the $13K minimum. Guard on
  // numberOfAssets > 0 (not raw cost) so a tiny count still hits $13K; 0 assets → $0.
  const floorAssetCost =
    numberOfAssets > 0
      ? Math.max(assetComputeCost * ASSET_FLOOR_MULTIPLIER, ASSET_SCAN_MINIMUM)
      : 0;
  // True when the platform line is clamped to the minimum floor. Naturally
  // false when numberOfAssets === 0 (floorAssetCost is 0 there).
  // The rate MUST be threaded through: atPlatformFloor gates requireMinimumTier on
  // the Intelligence step, so it has to agree with the floor number rendered next
  // to it — both must be computed on the same cost basis.
  const atPlatformFloor = isAtPlatformFloor({
    numberOfAssets,
    scanSchedule: inputs.scanSchedule,
    assetComputeAnnualRate: inputs.assetComputeAnnualRate,
  });
  // MSRP: 90% gross-margin list (10× cost), never below the $13K minimum.
  const msrpAssetCost =
    numberOfAssets > 0
      ? Math.max(assetComputeCost * ASSET_MSRP_MULTIPLIER, ASSET_SCAN_MINIMUM)
      : 0;

  // FQDN floor: flat rate × count × floor frequency multiplier (UNCHANGED)
  const floorFqdnCost = Math.round(
    numberOfApplications * FQDN_FLOOR_RATE * multiplier.floor
  );

  // AEV per-exercise pricing (Ultimate-class tiers only).
  // ludacris mirrors Ultimate pricing until product specifies otherwise.
  const isUltimate =
    inputs.selectedTier === 'ultimate' || inputs.selectedTier === 'ludacris';
  const numberOfAEVExercises = isUltimate
    ? inputs.numberOfAEVExercises || 0
    : 0;
  const floorAEVCost = Math.round(
    numberOfAEVExercises * AEV_EXERCISE_FLOOR_RATE
  );
  const msrpAEVCost = Math.round(
    numberOfAEVExercises * AEV_EXERCISE_FLOOR_RATE * MSRP_MULTIPLIER
  );

  // Red team pricing (ludacris-only). Flat rate — the same value applies to
  // BOTH floor and MSRP (no 2× spread).
  const isLudacris = inputs.selectedTier === 'ludacris';
  const numberOfRedTeams = isLudacris ? inputs.numberOfRedTeams || 0 : 0;
  const redTeamCost = Math.round(numberOfRedTeams * RED_TEAM_RATE);

  const floor = floorAssetCost + floorFqdnCost + floorAEVCost + redTeamCost;

  // FQDN MSRP: flat rate × MSRP_MULTIPLIER × MSRP frequency multiplier (UNCHANGED)
  const msrpFqdnCost = Math.round(
    numberOfApplications * FQDN_FLOOR_RATE * MSRP_MULTIPLIER * multiplier.msrp
  );
  const msrp = msrpAssetCost + msrpFqdnCost + msrpAEVCost + redTeamCost;

  return {
    floor,
    msrp,
    atPlatformFloor,
    breakdown: {
      fqdnCost: msrpFqdnCost,
      assetCost: msrpAssetCost,
      aevCost: msrpAEVCost,
      redTeamCost,
    },
    floorBreakdown: {
      fqdnCost: floorFqdnCost,
      assetCost: floorAssetCost,
      aevCost: floorAEVCost,
      redTeamCost,
    },
    aevFloor: floorAEVCost,
    aevMsrp: msrpAEVCost,
    redTeamFloor: redTeamCost,
    redTeamMsrp: redTeamCost,
  };
}

// Helper function to format currency
export function formatMSRP(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}
